<script setup lang="ts">
/**
 * First-visit reassurance popup: tells the visitor they are on the official
 * Smerra space, which replaces the old one. Mounted once at the app root;
 * whether it shows is decided by `useReassuranceScreen` (backend switch +
 * "already seen" flag). Every way out — close button, Escape, backdrop, either
 * CTA — dismisses it for good. Teleported to <body>, nothing rendered while
 * hidden, so SSG output is unaffected.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import AppButton from './AppButton.vue'
import logoUrl from '@/assets/logo.webp'
import imageUrl from '@/assets/reassurance.png'
import { useAuth } from '@/composables/useAuth'
import { useReassuranceScreen } from '@/composables/useReassuranceScreen'
import { useSheetScrollLock } from '@/composables/useSheetScrollLock'
import { accountHref, staticPageHref } from '@/router/products'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'

const { t, locale } = useI18n()
const route = useRoute()
const { isAuthenticated } = useAuth()
const { visible, load, dismiss } = useReassuranceScreen()

const titleId = 'reassurance-dialog-title'
const closeButton = ref<HTMLButtonElement | null>(null)

const currentLocale = computed(() => locale.value as Locale)
const memberHref = computed(() =>
  staticPageHref(isAuthenticated.value ? 'account' : 'login', currentLocale.value),
)
/** "Sign up online" target. A visitor already inside a subscription journey
 *  stays where they are (the CTA only closes the popup) rather than losing their
 *  answers; otherwise it leads to the product list (home for prospects, the
 *  "subscribe" section of the member area for connected members). */
const subscribeHref = computed<string | null>(() => {
  if (route.meta?.productKey) return null
  if (isAuthenticated.value) return accountHref('subscribe', currentLocale.value)
  return currentLocale.value === DEFAULT_LOCALE ? '/' : `/${currentLocale.value}/`
})

useSheetScrollLock(visible, dismiss)

// Move focus into the dialog as soon as it appears, for keyboard and screen-reader users.
watch(visible, async (open) => {
  if (!open) return
  await nextTick()
  closeButton.value?.focus()
})

// Client-only: the platform settings are never fetched during SSG pre-rendering.
onMounted(load)
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="rd-overlay" @click.self="dismiss">
      <div class="rd" role="dialog" aria-modal="true" :aria-labelledby="titleId">
        <button
          ref="closeButton"
          type="button"
          class="rd__close"
          :aria-label="t('reassurance.close')"
          @click="dismiss"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>

        <img class="rd__logo" :src="logoUrl" alt="Smerra" width="462" height="122" decoding="async" />

        <div class="rd__body">
          <div class="rd__card">
            <h2 :id="titleId" class="rd__title">{{ t('reassurance.title') }}</h2>
            <p class="rd__subtitle">{{ t('reassurance.subtitle') }}</p>
            <p class="rd__text">{{ t('reassurance.intro') }}</p>
            <p class="rd__text">{{ t('reassurance.purpose') }}</p>
            <p class="rd__text">{{ t('reassurance.teaser') }}</p>

            <div class="rd__actions">
              <AppButton as="a" :href="memberHref" variant="dark" :icon-right="false" ring @click="dismiss">
                {{ t('reassurance.memberArea') }}
              </AppButton>
              <AppButton
                v-if="subscribeHref"
                as="a"
                :href="subscribeHref"
                variant="dark"
                :icon-right="false"
                ring
                @click="dismiss"
              >
                {{ t('reassurance.subscribe') }}
              </AppButton>
              <AppButton v-else variant="dark" :icon-right="false" ring @click="dismiss">
                {{ t('reassurance.subscribe') }}
              </AppButton>
            </div>

            <p class="rd__notice">{{ t('reassurance.notice') }}</p>
          </div>

          <img
            class="rd__image"
            :src="imageUrl"
            :alt="t('reassurance.imageAlt')"
            width="650"
            height="400"
            decoding="async"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.rd-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal, 70);
  /* `margin: auto` on the panel (not grid centering) keeps its top reachable
     when it is taller than a small screen and the overlay scrolls. */
  display: flex;
  padding: var(--layout-gutter);
  overflow-y: auto;
  background: oklch(20% 0.02 345 / 0.45);
}
.rd {
  position: relative;
  width: 100%;
  max-width: 64rem;
  margin: auto;
  padding: clamp(1.25rem, 3vw, 2rem);
  background: var(--color-beige);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(1rem, 3vw, 2rem);
}
.rd__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--color-white);
  color: var(--color-chocolate);
  cursor: pointer;
}
.rd__close svg { width: 1.1rem; height: 1.1rem; }
.rd__close:focus-visible { outline: 2px solid var(--focus-ring); outline-offset: 2px; }
.rd__logo { height: 2.5rem; width: auto; }

/* Mobile first: the text card, then the image, stacked. */
.rd__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
}
.rd__card {
  position: relative;
  isolation: isolate;
  padding: clamp(1.25rem, 3vw, 2rem);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
/* Tilted white backdrop as on the mockup; only the background turns, the text stays straight. */
.rd__card::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background: var(--color-white);
  border-radius: var(--radius-lg);
  transform: rotate(-2deg);
}
.rd__title {
  font-size: var(--text-lg);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--primary);
}
.rd__subtitle { font-weight: 700; color: var(--primary); }
.rd__text { font-size: var(--text-sm); color: var(--text); line-height: var(--leading-normal); }
.rd__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
}
.rd__notice { font-size: var(--text-xs); font-style: italic; color: var(--text); }
.rd__image { width: 100%; max-width: 26rem; height: auto; }

@media (min-width: 56rem) {
  .rd__body { flex-direction: row; align-items: center; }
  .rd__card { flex: 1 1 55%; }
  .rd__image { flex: 1 1 45%; min-width: 0; }
}
</style>
